import { useState, useMemo } from 'react';
import { AlertCircle } from 'lucide-react';
import {
  DataGrid,
  FilterBar,
  MetricCard,
  MetricCardGrid,
  METRIC_READ_CAPTIONS,
  Skeleton,
} from '@aaimplatform/ui';
import type {
  FilterDefinition,
  ActiveFilter,
} from '@aaimplatform/ui';
import { usePledges } from '../../hooks/usePledges';
import type { PledgeSummary, PledgeStatus } from '../../hooks/usePledges';
import {
  BACK_TO_ACTIVE,
  CHA_TIER_LABEL,
  CHA_TIER_ORDER,
  CHA_TIER_UNCLASSIFIED_LABEL,
  PLEDGE_SEARCH_PLACEHOLDER,
  formatCurrencyFull,
  listCoverage,
  usePledgeSummaryColumns,
} from './pledge-display';

// ---------------------------------------------------------------------------
// Sprint C14 Lane B WP B-2: Active Pledges
//
// Companion to PledgeInbox. Shows pledges that have moved past inbox triage
// and are either (a) acknowledged by the FI but not yet executed, or (b)
// active and under ongoing CHA monitoring.
//
// Status filter: ACKNOWLEDGED + ACTIVE.
// CHA tier rollup column: read directly from PledgeSummary.currentChaTier
// (already populated by /api/admin/pledges -- the list mapping in
// services/platform-admin-api/src/routes/pledge.routes.ts). No separate
// /api/admin/cha-runtime call is required since the projection already
// carries the tier per pledge.
//
// The ladder is the canonical four tiers, GREEN / YELLOW / ORANGE / RED, with
// RED terminal (collateral-policy.openapi.yaml CollateralHealthTier; operator
// decision 2026-07-27, iris cw-06). currentChaTier is typed as that ladder on
// the wire, and platform-admin-api omits a stored value off it, so a pledge
// carries one of the four tiers or none; a pledge with none is unclassified.
// ---------------------------------------------------------------------------

const ACTIVE_STATUSES: ReadonlyArray<PledgeStatus> = ['ACKNOWLEDGED', 'ACTIVE'];

/** Filter value for a pledge whose projection carries no tier. */
const UNCLASSIFIED_FILTER_VALUE = 'UNCLASSIFIED';

// ---------------------------------------------------------------------------
// FilterBar
// ---------------------------------------------------------------------------

const FILTER_DEFINITIONS: FilterDefinition[] = [
  {
    id: 'chaTier',
    label: 'CHA Tier',
    type: 'select',
    options: [
      ...CHA_TIER_ORDER.map((tier) => ({ value: tier, label: CHA_TIER_LABEL[tier] })),
      { value: UNCLASSIFIED_FILTER_VALUE, label: CHA_TIER_UNCLASSIFIED_LABEL },
    ],
  },
  {
    id: 'status',
    label: 'Status',
    type: 'select',
    options: [
      { value: 'ACKNOWLEDGED', label: 'Acknowledged' },
      { value: 'ACTIVE', label: 'Active' },
    ],
  },
];

/**
 * Tier counts over a set of pledges: the four tiers of the ladder, plus
 * `unclassified` for a pledge whose projection carries no tier. The wire value
 * is typed CollateralHealthTier (iris cw-06), so there is no off-ladder count.
 */
export function rollupChaTiers(pledges: ReadonlyArray<PledgeSummary>): {
  GREEN: number;
  YELLOW: number;
  ORANGE: number;
  RED: number;
  unclassified: number;
} {
  const rollup = { GREEN: 0, YELLOW: 0, ORANGE: 0, RED: 0, unclassified: 0 };
  for (const p of pledges) {
    const tier = p.currentChaTier;
    if (tier) rollup[tier] += 1;
    else rollup.unclassified += 1;
  }
  return rollup;
}

/**
 * The distribution tile's description: the tier order its value follows, then
 * any pledge that carries no tier.
 */
export function chaDistributionDescription(
  rollup: Pick<ReturnType<typeof rollupChaTiers>, 'unclassified'>,
): string {
  return [
    CHA_TIER_ORDER.map((tier) => CHA_TIER_LABEL[tier]).join(' / '),
    rollup.unclassified > 0 ? `${rollup.unclassified} unclassified` : null,
  ]
    .filter(Boolean)
    .join('; ');
}

// ---------------------------------------------------------------------------
// ActivePledgesPage
// ---------------------------------------------------------------------------

export function ActivePledgesPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
  const columns = usePledgeSummaryColumns(BACK_TO_ACTIVE);

  // The /api/admin/pledges endpoint accepts a single status param. To cover
  // ACKNOWLEDGED + ACTIVE we fetch both pages and merge. React Query
  // dedupes both queries by key.
  const acknowledgedQuery = usePledges({ status: 'ACKNOWLEDGED' });
  const activeQuery = usePledges({ status: 'ACTIVE' });

  const isLoading = acknowledgedQuery.isLoading || activeQuery.isLoading;
  const error = acknowledgedQuery.error || activeQuery.error;

  // Both halves must be READ before any tile may describe the book. One half
  // failing leaves a merge that looks complete and is not; a paused first
  // fetch leaves no data and no error, which is not an empty book either.
  const bothRead = Boolean(acknowledgedQuery.data && activeQuery.data);
  const withheldCaption: string | undefined = error
    ? METRIC_READ_CAPTIONS.failed
    : bothRead
      ? undefined
      : METRIC_READ_CAPTIONS.unread;

  const allPledges = useMemo<PledgeSummary[]>(() => {
    const ack = acknowledgedQuery.data?.pledges ?? [];
    const act = activeQuery.data?.pledges ?? [];
    return [...ack, ...act];
  }, [acknowledgedQuery.data, activeQuery.data]);

  // Each status query reads one page (limit default 20). Summed over what the
  // two pages carried, the money tiles are a subtotal whenever either server
  // total is larger.
  const coverage = useMemo(() => {
    const ack = listCoverage(
      acknowledgedQuery.data?.pledges.length ?? 0,
      acknowledgedQuery.data?.total,
    );
    const act = listCoverage(activeQuery.data?.pledges.length ?? 0, activeQuery.data?.total);
    return {
      read: ack.read + act.read,
      total: (ack.total ?? ack.read) + (act.total ?? act.read),
      partial: ack.partial || act.partial,
    };
  }, [acknowledgedQuery.data, activeQuery.data]);

  // Apply chip filters (status, CHA tier) client-side after the merge.
  const statusChip = activeFilters.find((f) => f.filterId === 'status')
    ?.value as PledgeStatus | undefined;
  const chaTierChip = activeFilters.find((f) => f.filterId === 'chaTier')?.value;

  const filteredPledges = useMemo(() => {
    let rows = allPledges.filter((p) => ACTIVE_STATUSES.includes(p.status));

    if (statusChip) {
      rows = rows.filter((p) => p.status === statusChip);
    }
    if (chaTierChip) {
      if (chaTierChip === UNCLASSIFIED_FILTER_VALUE) {
        rows = rows.filter((p) => !p.currentChaTier);
      } else {
        rows = rows.filter((p) => p.currentChaTier === chaTierChip);
      }
    }
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      rows = rows.filter(
        (p) =>
          p.memberEmail.toLowerCase().includes(query) ||
          (p.memberName ?? '').toLowerCase().includes(query) ||
          p.collateralPackageId.toLowerCase().includes(query),
      );
    }
    return rows;
  }, [allPledges, statusChip, chaTierChip, searchQuery]);

  // ---------------------------------------------------------------------------
  // CHA tier rollup metrics
  // ---------------------------------------------------------------------------

  const chaRollup = useMemo(() => rollupChaTiers(filteredPledges), [filteredPledges]);

  const totalCollateral = useMemo(
    () =>
      filteredPledges.reduce((sum, p) => sum + (p.totalCollateralValue ?? 0), 0),
    [filteredPledges],
  );

  const totalPledgeable = useMemo(
    () =>
      filteredPledges.reduce(
        (sum, p) => sum + (p.totalPledgeableAmount ?? 0),
        0,
      ),
    [filteredPledges],
  );

  // RED is the terminal tier and the only at-risk tier on the ladder.
  const atRiskCount = chaRollup.RED;
  const coverageNote = coverage.partial
    ? `Counted from ${coverage.read} of ${coverage.total} pledges read`
    : undefined;

  const withheld = withheldCaption !== undefined;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div>
      {error && (
        <div className="bg-error/5 border border-error/20 rounded-sm p-4 mb-6 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-error flex-shrink-0" />
          <div className="text-sm font-sans text-error">
            Failed to load pledges: {error.message}
          </div>
        </div>
      )}

      <MetricCardGrid className="mb-6">
        {isLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-surface p-6 rounded-sm shadow-sm">
              <Skeleton className="h-10 w-12 mb-3" />
              <Skeleton className="h-3 w-24 mb-2" />
              <Skeleton className="h-3 w-16" />
            </div>
          ))
        ) : (
          <>
            {/* Each caption describes its figure, so it takes the sans
                description slot. The `change` slot is monospace and colored as
                a movement, and set "No pledges at Red" in green (fi-g1-15). The
                one alert-grade figure, a pledge at Red, carries the alert color
                on the value itself. */}
            <MetricCard
              label="Active pledges"
              value={withheld ? '--' : filteredPledges.length.toString()}
              caption={withheldCaption}
              description={
                withheld
                  ? undefined
                  : coverageNote ?? `${chaRollup.GREEN} healthy, ${atRiskCount} at risk`
              }
            />
            <MetricCard
              label="At risk (Red)"
              value={withheld ? '--' : atRiskCount.toString()}
              valueColor={!withheld && atRiskCount > 0 ? 'accent' : undefined}
              caption={withheldCaption}
              description={
                withheld
                  ? undefined
                  : atRiskCount > 0
                    ? 'For your institution\'s decision'
                    : 'No pledges at Red'
              }
            />
            {/* The row and detail formatter, so the tile and the row beneath
                it print one amount the same way; the compact form read
                $81,250 as "$81.3K" (fi-g1-10). */}
            <MetricCard
              label="Total pledgeable"
              value={withheld || coverage.partial ? '--' : formatCurrencyFull(totalPledgeable)}
              caption={withheldCaption}
              description={
                withheld
                  ? undefined
                  : coverageNote ?? `${formatCurrencyFull(totalCollateral)} collateral value`
              }
            />
            <MetricCard
              label="CHA distribution"
              value={
                withheld
                  ? '--'
                  : CHA_TIER_ORDER.map((tier) => chaRollup[tier]).join('/')
              }
              caption={withheldCaption}
              description={withheld ? undefined : chaDistributionDescription(chaRollup)}
            />
          </>
        )}
      </MetricCardGrid>

      <FilterBar
        filters={FILTER_DEFINITIONS}
        activeFilters={activeFilters}
        onFiltersChange={setActiveFilters}
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        // Short enough for the phone field; the placeholder doubles as the
        // input's accessible name (fi-g1-17).
        searchPlaceholder={PLEDGE_SEARCH_PLACEHOLDER}
        className="rounded-t-sm"
      />

      {isLoading ? (
        <div className="bg-surface rounded-sm shadow-sm p-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full mb-2" />
          ))}
        </div>
      ) : (
        <DataGrid<PledgeSummary>
          data={filteredPledges}
          columns={columns}
          emptyMessage={
            error
              ? 'Pledges could not be read, so none are listed.'
              : statusChip || chaTierChip || searchQuery.trim()
                ? 'No pledges match these filters or search.'
                : 'No pledges are acknowledged or active.'
          }
          enablePagination
          defaultPageSize={20}
          enableColumnResize
          hoverable
          striped
          getRowId={(row) => row.id}
          className="w-full rounded-b-sm shadow-sm"
        />
      )}
    </div>
  );
}
